import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Disc3 } from "lucide-react";

/**
 * Procedural electro-funk / popping loop generated with the Web Audio API.
 * No external audio dependency. Floating vinyl widget with play/mute.
 */
export function AudioPlayer() {
  const ctxRef = useRef(null);
  const gainRef = useRef(null);
  const schedRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [prompt, setPrompt] = useState(true);

  const ensureCtx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const g = ctx.createGain();
      g.gain.value = 0.5;
      g.connect(ctx.destination);
      ctxRef.current = ctx;
      gainRef.current = g;
    }
    return ctxRef.current;
  };

  const kick = (ctx, t) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.setValueAtTime(220, t);
    o.frequency.exponentialRampToValueAtTime(48, t + 0.09);
    g.gain.setValueAtTime(1.1, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.26);
    o.connect(g); g.connect(gainRef.current);
    o.start(t); o.stop(t + 0.28);
    const c = ctx.createOscillator(); const cg = ctx.createGain();
    c.type = "square"; c.frequency.setValueAtTime(1200, t); c.frequency.exponentialRampToValueAtTime(200, t + 0.02);
    cg.gain.setValueAtTime(0.25, t); cg.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
    c.connect(cg); cg.connect(gainRef.current); c.start(t); c.stop(t + 0.04);
  };

  const noiseBuf = (ctx, dur, curve = 2) => {
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, curve);
    return buf;
  };

  const clap = (ctx, t) => {
    [0, 0.012, 0.026].forEach((off, i) => {
      const src = ctx.createBufferSource(); src.buffer = noiseBuf(ctx, i === 2 ? 0.22 : 0.03, 1.5);
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 1500; bp.Q.value = 0.8;
      const g = ctx.createGain();
      g.gain.setValueAtTime(i === 2 ? 0.7 : 0.45, t + off);
      g.gain.exponentialRampToValueAtTime(0.001, t + off + (i === 2 ? 0.2 : 0.03));
      src.connect(bp); bp.connect(g); g.connect(gainRef.current);
      src.start(t + off); src.stop(t + off + 0.25);
    });
  };

  const hat = (ctx, t, open = false) => {
    const src = ctx.createBufferSource(); src.buffer = noiseBuf(ctx, open ? 0.18 : 0.05, 1);
    const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 8000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(open ? 0.16 : 0.13, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + (open ? 0.16 : 0.035));
    src.connect(hp); hp.connect(g); g.connect(gainRef.current);
    src.start(t); src.stop(t + 0.2);
  };

  const bass = (ctx, t, freq, len = 0.18) => {
    const o = ctx.createOscillator();
    const f = ctx.createBiquadFilter();
    const g = ctx.createGain();
    o.type = "sawtooth";
    o.frequency.value = freq;
    f.type = "lowpass"; f.Q.value = 9;
    f.frequency.setValueAtTime(1800, t);
    f.frequency.exponentialRampToValueAtTime(140, t + len);
    g.gain.setValueAtTime(0.0, t);
    g.gain.linearRampToValueAtTime(0.32, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.001, t + len + 0.05);
    o.connect(f); f.connect(g); g.connect(gainRef.current);
    o.start(t); o.stop(t + len + 0.08);
  };

  const stab = (ctx, t, freqs) => {
    freqs.forEach((fq) => {
      const o = ctx.createOscillator(); const g = ctx.createGain(); const f = ctx.createBiquadFilter();
      o.type = "square"; o.frequency.value = fq; o.detune.value = (Math.random() - 0.5) * 12;
      f.type = "lowpass"; f.frequency.setValueAtTime(3500, t); f.frequency.exponentialRampToValueAtTime(600, t + 0.12);
      g.gain.setValueAtTime(0.07, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
      o.connect(f); f.connect(g); g.connect(gainRef.current); o.start(t); o.stop(t + 0.16);
    });
  };

  const start = () => {
    const ctx = ensureCtx();
    if (ctx.state === "suspended") ctx.resume();
    const bpm = 108;
    const step = 60 / bpm / 4; // 16th notes
    let n = 0;
    let next = ctx.currentTime + 0.1;
    // electro-funk popping groove (E minor) : 16-step patterns
    const kicks = [1,0,0,0, 0,0,1,0, 0,0,1,0, 0,0,0,0];
    const kicks2 = [1,0,0,0, 0,0,1,0, 0,0,1,0, 0,0,0,1];
    const claps = [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0];
    const bassNotes = [41.2,0,0,41.2, 0,0,82.4,0, 0,41.2,0,0, 61.7,0,55,0]; // E funk line with octave slap
    const stabSteps = { 4: [329.6, 392, 493.9], 11: [293.7, 349.2, 440], 14: [329.6, 392, 493.9] };
    const loop = () => {
      while (next < ctx.currentTime + 0.25) {
        const s = n % 16;
        const bar = Math.floor(n / 16) % 4;
        hat(ctx, next, s % 4 === 2);
        if ((bar === 3 ? kicks2 : kicks)[s]) kick(ctx, next);
        if (claps[s]) clap(ctx, next);
        if (bar === 3 && s === 15) clap(ctx, next);
        if (bassNotes[s]) bass(ctx, next, bassNotes[s], s === 6 ? 0.1 : 0.18);
        if (stabSteps[s] && bar % 2 === 1) stab(ctx, next, stabSteps[s]);
        next += step;
        n++;
      }
      schedRef.current = requestAnimationFrame(loop);
    };
    loop();
    setPlaying(true);
    setPrompt(false);
  };

  const stop = () => {
    if (schedRef.current) cancelAnimationFrame(schedRef.current);
    if (ctxRef.current) ctxRef.current.suspend();
    setPlaying(false);
  };

  const toggle = () => (playing ? stop() : start());

  const toggleMute = () => {
    if (!gainRef.current) return;
    const m = !muted;
    gainRef.current.gain.value = m ? 0 : 0.5;
    setMuted(m);
  };

  useEffect(() => () => { if (schedRef.current) cancelAnimationFrame(schedRef.current); }, []);

  return (
    <>
      {prompt && (
        <button
          data-testid="audio-prompt-banner"
          onClick={start}
          className="fixed bottom-24 right-4 md:right-6 z-50 glass rounded-full px-4 py-2 text-xs font-mono2 text-mustard border border-mustard/40 animate-fade-in hover:bg-mustard/10 transition"
        >
          ▶ Activer le son Hip-Hop
        </button>
      )}
      <div
        data-testid="audio-player-widget"
        className="fixed bottom-4 right-4 md:right-6 z-50 glass rounded-full flex items-center gap-1 pr-2 pl-1 py-1 shadow-2xl"
      >
        <button
          data-testid="audio-play-toggle"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="relative w-11 h-11 rounded-full bg-ink flex items-center justify-center border border-mustard/40 overflow-hidden"
        >
          <Disc3 className={`w-8 h-8 text-mustard ${playing && !muted ? "spin-vinyl" : ""}`} strokeWidth={1.5} />
          <span className="absolute inset-0 flex items-center justify-center">
            {playing ? <Pause className="w-4 h-4 text-cream" fill="currentColor" /> : <Play className="w-4 h-4 text-cream ml-0.5" fill="currentColor" />}
          </span>
        </button>
        <div className="hidden sm:flex items-end gap-[3px] h-6 px-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="eq-bar"
              style={{
                background: "var(--mustard)",
                animationPlayState: playing && !muted ? "running" : "paused",
                animationDelay: `${i * 0.12}s`,
                height: playing && !muted ? undefined : "20%",
              }}
            />
          ))}
        </div>
        <button
          data-testid="audio-mute-toggle"
          onClick={toggleMute}
          aria-label={muted ? "Activer le son" : "Couper le son"}
          className="w-9 h-9 rounded-full flex items-center justify-center text-cream hover:text-mustard transition"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </>
  );
}
